import { forwardRef } from "react";
import { clsx } from "clsx";
import type { SelectProps } from "./SelectProps";
import classes from "./Select.module.scss";
import { ChevronDown } from "@/components/ui/Icon/ChevronDown";
import { useFormControl, useErrorDescribedBy } from "@/hooks/useFormControl";
import { CheckSharp } from "@/components/ui/Icon/CheckSharp";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(props, ref) {
    const {
      children,
      className,
      size = "medium",
      hasError,
      id,
      "aria-describedby": describedByProp,
      ...htmlProps
    } = props;

    const formControl = useFormControl();
    const describedBy = useErrorDescribedBy(describedByProp);
    const selectId = id ?? formControl?.id;
    const selectHasError = hasError ?? formControl?.hasError ?? false;
    const isValid = formControl?.isValid && !selectHasError;

    return (
      <div className={clsx(classes.wrapper, className)}>
        <select
          {...htmlProps}
          id={selectId}
          className={clsx(classes.select, classes[size], {
            [classes.isValid]: isValid,
          })}
          {...(selectHasError && { "aria-invalid": true })}
          aria-describedby={describedBy}
          ref={ref}
        >
          {children}
        </select>

        <div className={classes.icons}>
          <div className={classes.chevronWrapper}>
            <ChevronDown size={24} color="currentColor" />
          </div>

          {isValid && (
            <div className={classes.checkWrapper}>
              <CheckSharp
                size={24}
                strokeWidth={4}
                stroke="currentColor"
                data-testid="select-valid-icon"
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);
